import type { CheckDepthTier } from '@nestjs-boilerplate/types';
import { SERVICE_KEY_PAID_PROPERTY, TIER_INCLUDED_SERVICES } from '@nestjs-boilerplate/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { riskAssessmentKeys } from '@/features/dashboard/api/risk-assessments';
import {
  BalanceDisplay,
  deductBalance,
  formatPence,
  InsufficientBalance,
  refundTransaction,
  SERVICES,
  TIERS,
  useCurrentBalance,
  useServicePrices,
} from '@/features/payments';
import { apiClient } from '@/lib/api-client';
import { useFeatureFlag } from '@/lib/use-feature-flag';
import { addGeneratingIdAtom, removeGeneratingIdAtom } from '@/stores/generating.store';
import { resetWizardStateAtom, setSelectedTierAtom, wizardStateAtom } from '@/stores/wizard.store';

import {
  getScreeningStatus,
  useClientDetails,
  useScreeningStatus,
  wizardKeys,
} from '../api/risk-assessment-wizard';
import { TIER_CONFIGS } from '../constants/tier-config';
import { normalizeResumeStep, type PaymentFlowStep } from '../hooks/use-payment-flow';
import { getTierPricePence, PRICE_LOADING_PLACEHOLDER } from '../lib/tier-pricing';

import { CheckDepthSelector } from './check-depth-selector';
import { PaymentFlowModals } from './payment-flow-modals';
import { WizardLayout } from './wizard-layout';

interface DepthSelectionFormProps {
  assessmentId: string;
}

/** Helper to map database step values to PaymentFlowStep */
function mapDbStepToFlowStep(dbStep: string | null): PaymentFlowStep | null {
  if (!dbStep) return null;
  const mapping: Record<string, PaymentFlowStep> = {
    REQUEST_DOCS: 'REQUEST_DOCS',
    MATCH_REPORT: 'MATCH_REPORT',
    EDD_UPSELL: 'EDD_UPSELL',
    EDD: 'EDD',
    DOWNLOAD_REPORT: 'DOWNLOAD_REPORT',
  };
  return mapping[dbStep] || null;
}

/**
 * Step 3: Due Diligence Level selection page.
 * Allows users to choose between Basic, Standard, or Enhanced tiers.
 * Handles payment and screening flow after selection.
 */
export function DepthSelectionForm({ assessmentId }: DepthSelectionFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const wizardState = useAtomValue(wizardStateAtom);
  const setSelectedTier = useSetAtom(setSelectedTierAtom);
  const resetWizardState = useSetAtom(resetWizardStateAtom);
  const addGeneratingId = useSetAtom(addGeneratingIdAtom);
  const removeGeneratingId = useSetAtom(removeGeneratingIdAtom);
  const { enabled: documentUploadEnabled } = useFeatureFlag('document_upload');

  const { data: clientDetails, isLoading: isLoadingClient } = useClientDetails(assessmentId);
  const {
    data: balanceData,
    isLoading: isLoadingBalance,
    refetch: refetchBalance,
  } = useCurrentBalance();
  const { data: servicePrices, isLoading: isLoadingPricing } = useServicePrices();

  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isScreeningPending, setIsScreeningPending] = useState(false);
  const [isEddPending, setIsEddPending] = useState(false);
  const [flowStep, setFlowStep] = useState<PaymentFlowStep>('IDLE');
  const [riskLevel, setRiskLevel] = useState<'LOW' | 'MEDIUM' | 'HIGH'>('LOW');
  const [purchasedServices, setPurchasedServices] = useState({
    initialScreening: false,
    matchReport: false,
    eddReport: false,
  });

  // Track whether we've seen PROCESSING status since entering REPORT_GENERATING
  const hasSeenProcessingRef = useRef(false);
  // Track whether we've initialized from saved state
  const hasInitializedRef = useRef(false);
  // Prevent rapid double-clicks on payment buttons (ref updates synchronously, unlike state)
  const isPaymentInFlightRef = useRef(false);

  const selectedChecks = wizardState.selectedChecks;

  // Only poll during active screening
  const shouldPollStatus = flowStep === 'SCREENING_PROGRESS' || flowStep === 'REPORT_GENERATING';
  const { data: polledScreeningStatus } = useScreeningStatus(assessmentId, shouldPollStatus);

  // Fetch once on mount for IDLE state (to restore flow state after refresh/re-login)
  const { data: initialStatus, isLoading: isLoadingStatus } = useQuery({
    queryKey: wizardKeys.screeningStatus(assessmentId),
    queryFn: () => getScreeningStatus(assessmentId),
    enabled: flowStep === 'IDLE' && !!assessmentId,
    staleTime: 60000, // Cache for 1 minute
  });

  // Use whichever status is available (polled during active flow, initial in IDLE)
  const screeningStatus = polledScreeningStatus ?? initialStatus;

  // Get the selected tier from wizard state, or derive from paid services after refresh
  const selectedTier = useMemo(() => {
    // If Jotai state has a tier, use it
    if (wizardState.selectedTier) return wizardState.selectedTier;

    // After refresh, derive from paid services
    if (screeningStatus?.paidServices) {
      if (screeningStatus.paidServices.eddReport) return 'enhanced';
      if (screeningStatus.paidServices.matchReport) return 'standard';
      if (screeningStatus.paidServices.initialScreening) return 'basic';
    }

    return null;
  }, [wizardState.selectedTier, screeningStatus?.paidServices]);

  // Calculate pricing info for selected tier. requiredAmountPence is null while
  // the Pricing table hasn't loaded or any required service row is missing/inactive —
  // never fall back to 0 (would make `currentBalance >= 0` allow purchase with no funds).
  const selectedTierConfig = selectedTier ? TIER_CONFIGS[selectedTier] : null;
  const requiredAmountPence = selectedTierConfig
    ? getTierPricePence(selectedTierConfig, servicePrices)
    : null;
  const currentBalancePence = balanceData?.balancePence ?? 0;
  const hasSufficientBalance =
    requiredAmountPence !== null && currentBalancePence >= requiredAmountPence;
  const shortfall = requiredAmountPence !== null ? requiredAmountPence - currentBalancePence : 0;

  // Build client name for display
  const clientName = useMemo(() => {
    if (!clientDetails) return '';
    const parts = [clientDetails.forename, clientDetails.middleName, clientDetails.surname].filter(
      Boolean,
    );
    return parts.join(' ');
  }, [clientDetails]);

  // Initialize payment flow from saved state when screening status loads (for resumption after refresh/re-login)
  useEffect(() => {
    if (!screeningStatus || flowStep !== 'IDLE') return;
    if (hasInitializedRef.current) return;

    // Try to initialize from saved payment flow step
    const resumeStep = normalizeResumeStep(
      mapDbStepToFlowStep(screeningStatus.paymentFlowStep),
      documentUploadEnabled,
      {
        matchReport: screeningStatus.paidServices?.matchReport || false,
        eddReport: screeningStatus.paidServices?.eddReport || false,
      },
    );

    if (resumeStep) {
      hasInitializedRef.current = true;
      setFlowStep(resumeStep);
      setRiskLevel(screeningStatus.riskLevel || 'LOW');
      setPurchasedServices({
        initialScreening: screeningStatus.paidServices?.initialScreening || false,
        matchReport: screeningStatus.paidServices?.matchReport || false,
        eddReport: screeningStatus.paidServices?.eddReport || false,
      });
      return;
    }

    // If no saved step but user has paid for screening, determine the appropriate modal
    const screeningCompleted = screeningStatus.status === 'COMPLETED';
    const screeningFailed = screeningStatus.status === 'FAILED';
    const paidForScreening = screeningStatus.paidServices?.initialScreening;
    const paidForMatchReport = screeningStatus.paidServices?.matchReport;
    const paidForEdd = screeningStatus.paidServices?.eddReport;

    // If user has paid for screening but it hasn't completed yet, show progress modal
    // This covers NOT_STARTED (job queued), FAST_CHECKS_DONE, PROCESSING, and GENERATING_REPORT
    if (paidForScreening && !screeningCompleted && !screeningFailed) {
      hasInitializedRef.current = true;
      setPurchasedServices({
        initialScreening: true,
        matchReport: paidForMatchReport || false,
        eddReport: paidForEdd || false,
      });
      setFlowStep('SCREENING_PROGRESS');
      return;
    }

    if (screeningCompleted && paidForScreening) {
      hasInitializedRef.current = true;
      setRiskLevel(screeningStatus.riskLevel || 'LOW');
      setPurchasedServices({
        initialScreening: true,
        matchReport: paidForMatchReport || false,
        eddReport: paidForEdd || false,
      });

      // Determine which modal to show based on what's been purchased
      if (paidForEdd) {
        // Already paid for everything, go to report
        setFlowStep('COMPLETE');
      } else if (paidForMatchReport) {
        // Paid for match report but not EDD, show EDD upsell
        setFlowStep('EDD_UPSELL');
      } else {
        // Only paid for screening, show download report first
        setFlowStep('DOWNLOAD_REPORT');
      }
    }
  }, [screeningStatus, flowStep, documentUploadEnabled]);

  // Handle screening status changes
  useEffect(() => {
    if (!screeningStatus) return;

    // Don't advance if screening mutation is still pending (prevents stale cache from triggering advance)
    if (isScreeningPending) return;

    // Track PROCESSING or GENERATING_REPORT status during screening (for Enhanced tier and REPORT_GENERATING flow)
    if (
      (flowStep === 'SCREENING_PROGRESS' || flowStep === 'REPORT_GENERATING') &&
      (screeningStatus.status === 'PROCESSING' || screeningStatus.status === 'GENERATING_REPORT')
    ) {
      hasSeenProcessingRef.current = true;
      // Clear EDD pending flag once we've confirmed EDD is processing
      if (isEddPending) {
        setIsEddPending(false);
      }
    }

    // When screening completes during SCREENING_PROGRESS, move to next step based on tier
    if (flowStep === 'SCREENING_PROGRESS' && screeningStatus.status === 'COMPLETED') {
      const detectedRiskLevel = screeningStatus.riskLevel || 'LOW';
      setRiskLevel(detectedRiskLevel);

      if (selectedTier === 'basic') {
        // Basic: show download report modal first
        setFlowStep('DOWNLOAD_REPORT');
      } else if (selectedTier === 'standard') {
        // Standard: already paid for match report, show EDD upsell
        setFlowStep('EDD_UPSELL');
      } else {
        // Enhanced: already paid for everything, show the report ready modal
        // Must have seen PROCESSING status to ensure DDE actually ran
        if (hasSeenProcessingRef.current) {
          removeGeneratingId(assessmentId);
          // Show the "Report Generated Successfully" modal instead of going directly to report
          setFlowStep('REPORT_GENERATING');
        }
        // If we haven't seen PROCESSING yet, stay in SCREENING_PROGRESS
        // (the status will update to PROCESSING when adverse media starts)
      }
    }

    // When EDD completes during REPORT_GENERATING (upgrade flow or Enhanced tier), show completion
    // The ScreeningModal will show "Report Generated Successfully" and user can click "View Report"
    // No automatic navigation - let user click the button
  }, [
    screeningStatus,
    flowStep,
    selectedTier,
    assessmentId,
    removeGeneratingId,
    isScreeningPending,
    isEddPending,
  ]);

  // Navigate to report when flow completes
  useEffect(() => {
    if (flowStep === 'COMPLETE') {
      resetWizardState();
      navigate({ to: '/risk-assessment/$id/report', params: { id: assessmentId } });
    }
  }, [flowStep, assessmentId, navigate, resetWizardState]);

  // Reset processing flag when not in REPORT_GENERATING
  useEffect(() => {
    if (flowStep !== 'REPORT_GENERATING') {
      hasSeenProcessingRef.current = false;
    }
  }, [flowStep]);

  const handleTierSelect = (tier: CheckDepthTier) => {
    setSelectedTier(tier);
  };

  const handleBack = () => {
    navigate({ to: '/risk-assessment/$id/list-selection', params: { id: assessmentId } });
  };

  const handleCancel = () => {
    resetWizardState();
    navigate({ to: '/' });
  };

  const handleConfirmAndPay = async () => {
    // Guard against rapid double-clicks (ref check is synchronous)
    if (isPaymentInFlightRef.current) return;
    if (
      !selectedTier ||
      requiredAmountPence === null ||
      !hasSufficientBalance ||
      selectedChecks.length === 0
    ) {
      return;
    }

    // Set ref immediately to block concurrent calls
    isPaymentInFlightRef.current = true;
    setIsProcessing(true);
    const transactionIds: string[] = [];

    try {
      // One flat tier charge — the backend unlocks every service the tier includes
      // (initialScreening for all; +matchReport for standard; +matchReport+eddReport
      // for enhanced) against the same transactionId.
      const tierKey = TIERS[selectedTier];
      const adverseMediaMode = selectedTier === 'enhanced' ? 'dde' : 'light';

      const result = await deductBalance(tierKey, assessmentId);
      transactionIds.push(result.transactionId);

      await apiClient.fetch(`/risk-assessments/${assessmentId}/record-paid-service`, {
        method: 'POST',
        body: JSON.stringify({ serviceKey: tierKey, transactionId: result.transactionId }),
      });

      // Derive flags from the shared TIER_INCLUDED_SERVICES map so this list
      // can never drift from the backend's recordPaidService behaviour. Adding a
      // service to a tier updates both sides automatically.
      //
      // docRequest is intentionally not part of purchasedServices on this screen
      // because document requests are a separate a la carte flow, never part of a
      // tier purchase. Validate upfront — the state updater stays pure so it is
      // safe to re-invoke under React's Concurrent rendering.
      const includedServices = TIER_INCLUDED_SERVICES[tierKey];
      for (const svc of includedServices) {
        if (SERVICE_KEY_PAID_PROPERTY[svc] === 'docRequest') {
          throw new Error(
            `Tier ${tierKey} now includes ${svc} (docRequest) — extend purchasedServices to track it.`,
          );
        }
      }
      setPurchasedServices((prev) => {
        const next = { ...prev };
        for (const svc of includedServices) {
          const prop = SERVICE_KEY_PAID_PROPERTY[svc];
          // Narrowing only — the upfront check above already throws on docRequest.
          if (prop !== 'docRequest') next[prop] = true;
        }
        return next;
      });

      // Reset screening status cache before showing progress
      await queryClient.resetQueries({ queryKey: wizardKeys.screeningStatus(assessmentId) });

      // Show screening progress and mark screening as pending
      setFlowStep('SCREENING_PROGRESS');
      setIsProcessing(false);
      setIsScreeningPending(true);
      addGeneratingId(assessmentId);

      // Start the screening
      await apiClient.fetch(`/risk-assessments/${assessmentId}/screen`, {
        method: 'POST',
        body: JSON.stringify({ enabledChecks: selectedChecks, adverseMediaMode }),
      });

      // Screening API call completed - allow status changes to advance the flow
      setIsScreeningPending(false);

      // Invalidate queries - include balance so subsequent modals have fresh data
      queryClient.invalidateQueries({ queryKey: wizardKeys.assessment(assessmentId) });
      queryClient.invalidateQueries({ queryKey: wizardKeys.screeningStatus(assessmentId) });
      queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['balance'] });

      // Reset ref so subsequent purchase buttons work
      isPaymentInFlightRef.current = false;
    } catch (error) {
      // Attempt refund of ALL successful transactions if any step failed
      if (transactionIds.length > 0) {
        try {
          await Promise.all(transactionIds.map((txId) => refundTransaction(txId)));
          queryClient.invalidateQueries({ queryKey: ['balance'] });
          toast.error('Payment failed. Your payments have been refunded.');
        } catch {
          toast.error('Payment failed and refunds could not be processed. Please contact support.');
        }
      } else {
        toast.error(error instanceof Error ? error.message : 'Payment failed');
      }
      setIsProcessing(false);
      setIsScreeningPending(false);
      removeGeneratingId(assessmentId);
      // Reset ref so user can retry after error
      isPaymentInFlightRef.current = false;
    }
  };

  // Handle proceeding from download report (Basic tier)
  const handleProceedFromDownload = () => {
    setFlowStep('MATCH_REPORT');
  };

  // Handle Match Report purchase
  const handleMatchReportPurchase = async () => {
    if (isPaymentInFlightRef.current) return;
    isPaymentInFlightRef.current = true;
    setIsProcessing(true);
    try {
      const result = await deductBalance(SERVICES.MATCH_RISK_REPORT, assessmentId);
      await apiClient.fetch(`/risk-assessments/${assessmentId}/record-paid-service`, {
        method: 'POST',
        body: JSON.stringify({
          serviceKey: SERVICES.MATCH_RISK_REPORT,
          transactionId: result.transactionId,
        }),
      });
      await queryClient.invalidateQueries({ queryKey: wizardKeys.screeningStatus(assessmentId) });
      await refetchBalance();
      setPurchasedServices((prev) => ({ ...prev, matchReport: true }));
      setFlowStep('EDD_UPSELL');
      toast.success('Match Report purchased');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Purchase failed');
    } finally {
      setIsProcessing(false);
      isPaymentInFlightRef.current = false;
    }
  };

  // Handle EDD purchase
  const handleEddPurchase = async () => {
    if (isPaymentInFlightRef.current) return;
    isPaymentInFlightRef.current = true;
    setIsProcessing(true);
    try {
      const result = await deductBalance(SERVICES.AI_CONFIDENCE_REPORT, assessmentId);
      await apiClient.fetch(`/risk-assessments/${assessmentId}/record-paid-service`, {
        method: 'POST',
        body: JSON.stringify({
          serviceKey: SERVICES.AI_CONFIDENCE_REPORT,
          transactionId: result.transactionId,
        }),
      });

      // Reset the processing flag and mark EDD as pending BEFORE triggering upgrade
      // This ensures we wait for the NEW EDD processing to complete
      // (isEddPending prevents completion logic from firing on stale COMPLETED status)
      hasSeenProcessingRef.current = false;
      setIsEddPending(true);

      // Reset screening status cache to avoid stale COMPLETED status
      await queryClient.resetQueries({ queryKey: wizardKeys.screeningStatus(assessmentId) });

      // Trigger EDD upgrade
      await apiClient.fetch(`/risk-assessments/${assessmentId}/upgrade-to-edd`, {
        method: 'POST',
        body: JSON.stringify({}),
      });

      await refetchBalance();
      setPurchasedServices((prev) => ({ ...prev, eddReport: true }));
      setFlowStep('REPORT_GENERATING');
      toast.success('Enhanced Due Diligence started');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Purchase failed');
      setIsEddPending(false);
    } finally {
      setIsProcessing(false);
      isPaymentInFlightRef.current = false;
    }
  };

  // Skip handlers
  const handleSkipMatchReport = () => {
    if (!documentUploadEnabled) {
      handleSkipDocRequest();
      return;
    }
    setFlowStep('REQUEST_DOCS');
  };
  const handleSkipEddUpsell = async () => {
    try {
      await apiClient.fetch(`/risk-assessments/${assessmentId}/send-report-email`, {
        method: 'POST',
      });
    } catch (error) {
      console.warn('Failed to send report email:', error);
    }
    setFlowStep('REPORT_GENERATING');
  };
  const handleSkipEdd = () => {
    if (!documentUploadEnabled) {
      handleSkipDocRequest();
      return;
    }
    setFlowStep('REQUEST_DOCS');
  };
  const handleSkipDocRequest = async () => {
    if (!purchasedServices.matchReport && !purchasedServices.eddReport) {
      try {
        await apiClient.fetch(`/risk-assessments/${assessmentId}/complete-without-report`, {
          method: 'POST',
        });
        queryClient.invalidateQueries({ queryKey: wizardKeys.assessment(assessmentId) });
        queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
      } catch (error) {
        console.warn('Failed to complete assessment without report:', error);
      }
    }
    setFlowStep(
      purchasedServices.matchReport || purchasedServices.eddReport
        ? 'REPORT_GENERATING'
        : 'COMPLETE',
    );
  };

  const handleDocRequest = async (
    email: string,
    documentTypes: string[],
    otherDescription?: string,
  ) => {
    setIsProcessing(true);
    try {
      const result = await deductBalance(SERVICES.DOCUMENTATION_REQUEST, assessmentId);
      await apiClient.fetch(`/risk-assessments/${assessmentId}/record-paid-service`, {
        method: 'POST',
        body: JSON.stringify({
          serviceKey: SERVICES.DOCUMENTATION_REQUEST,
          transactionId: result.transactionId,
        }),
      });
      await apiClient.fetch(`/risk-assessments/${assessmentId}/generate-document-link`, {
        method: 'POST',
        body: JSON.stringify({ assessmentId, clientEmail: email, documentTypes, otherDescription }),
      });
      toast.success('Document request link sent');
      const hasPurchasedReport = purchasedServices.matchReport || purchasedServices.eddReport;
      if (!hasPurchasedReport) {
        await apiClient.fetch(`/risk-assessments/${assessmentId}/complete-without-report`, {
          method: 'POST',
        });
      }
      setFlowStep(hasPurchasedReport ? 'REPORT_GENERATING' : 'COMPLETE');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Document request failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGoBackToPurchase = () => setFlowStep('MATCH_REPORT');
  const handleBackToDashboard = async () => {
    resetWizardState();
    removeGeneratingId(assessmentId);
    // Refresh the dashboard lists so an in-progress/generating screening shows in
    // the "In Progress Risk Assessments" section on arrival, without a manual
    // refresh (the global 5m staleTime would otherwise serve a stale cached list).
    await queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
    navigate({ to: '/' });
  };

  // Show loading while fetching client details or screening status (needed for flow restoration)
  if (isLoadingClient || (isLoadingStatus && flowStep === 'IDLE')) {
    return (
      <WizardLayout currentStep={3} completedSteps={[1, 2]}>
        <div className="flex h-full items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </WizardLayout>
    );
  }

  // Check if user has already paid for screening (resuming after refresh)
  const hasAlreadyPaidForScreening = screeningStatus?.paidServices?.initialScreening;
  const screeningInProgressOrCompleted =
    screeningStatus?.status === 'FAST_CHECKS_DONE' ||
    screeningStatus?.status === 'PROCESSING' ||
    screeningStatus?.status === 'GENERATING_REPORT' ||
    screeningStatus?.status === 'COMPLETED';

  // If no checks were selected AND user hasn't already started screening, redirect back to list selection
  // (When resuming after refresh, checks may be empty but screening is already in progress)
  if (
    wizardState.selectedChecks.length === 0 &&
    !hasAlreadyPaidForScreening &&
    !screeningInProgressOrCompleted
  ) {
    return (
      <WizardLayout currentStep={3} completedSteps={[1, 2]}>
        <div className="space-y-8 px-8 pb-8">
          <section>
            <h1 className="mb-4 text-[24px] font-semibold text-[#051923]">No Checks Selected</h1>
            <p className="mb-4 text-[16px] text-[#051923]">
              Please select at least one screening check before choosing a due diligence level.
            </p>
            <Button
              onClick={handleBack}
              className="h-[45px] rounded-[5px] bg-[#131313] px-6 text-[16px] font-medium text-white hover:bg-[#131313]/90"
            >
              Back to List Selection
            </Button>
          </section>
        </div>
      </WizardLayout>
    );
  }

  return (
    <WizardLayout currentStep={3} completedSteps={[1, 2]}>
      <div className="space-y-8 px-8 pb-8">
        {/* Header Section */}
        <section>
          <h1 className="mb-4 text-[24px] font-semibold text-[#051923]">
            Select Due Diligence Level
          </h1>
          <p className="mb-4 text-[16px] text-[#051923]">
            Choose the level of due diligence for your screening of {clientName || 'the client'}.
          </p>
          <p className="text-[14px] leading-relaxed text-[#6B7280]">
            Select the tier that best matches your compliance requirements. Higher tiers include all
            features from lower tiers plus additional analysis and reporting capabilities.
          </p>
        </section>

        {/* Tier Selection */}
        <section>
          <CheckDepthSelector selectedTier={selectedTier} onSelect={handleTierSelect} />
        </section>

        {/* Balance and Payment Info */}
        {selectedTier && (
          <section className="space-y-4">
            <div className="rounded-lg border border-[#E5E7EB] p-4">
              <div className="flex items-center justify-between">
                <span className="text-[16px] font-medium text-[#051923]">
                  {selectedTierConfig?.name} Due Diligence
                </span>
                <span className="text-[18px] font-semibold text-[#131313]">
                  {requiredAmountPence !== null
                    ? formatPence(requiredAmountPence)
                    : PRICE_LOADING_PLACEHOLDER}
                </span>
              </div>
            </div>

            {!isLoadingBalance &&
              requiredAmountPence !== null &&
              (hasSufficientBalance ? (
                <BalanceDisplay currentBalancePence={currentBalancePence} />
              ) : (
                <InsufficientBalance
                  currentBalancePence={currentBalancePence}
                  requiredAmountPence={requiredAmountPence}
                  shortfall={shortfall}
                  onAddFunds={() => setShowAddFundsModal(true)}
                />
              ))}
          </section>
        )}
      </div>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 z-10 border-t border-[#D4DAE0] bg-white px-4 py-4 md:px-8">
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={handleBack}
            disabled={isProcessing || flowStep !== 'IDLE'}
            className="text-center text-[14px] text-[#051923] underline hover:no-underline disabled:opacity-50 sm:text-left"
          >
            Back
          </button>
          <div className="flex items-center gap-3 sm:gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isProcessing || flowStep !== 'IDLE'}
              className="h-[45px] flex-1 rounded-[5px] border-[#131313] px-6 text-[16px] font-medium text-[#131313] hover:bg-gray-50 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmAndPay}
              disabled={
                !selectedTier ||
                requiredAmountPence === null ||
                !hasSufficientBalance ||
                isProcessing ||
                isLoadingBalance ||
                isLoadingPricing ||
                flowStep !== 'IDLE'
              }
              className="h-[45px] flex-1 rounded-[5px] bg-[#131313] px-6 text-[16px] font-medium text-white hover:bg-[#131313]/90 sm:flex-none"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Confirm & Pay'
              )}
            </Button>
          </div>
        </div>
      </footer>

      {/* Payment Flow Modals */}
      <PaymentFlowModals
        flowStep={flowStep}
        assessmentId={assessmentId}
        screeningStatus={screeningStatus}
        isScreeningPending={isScreeningPending}
        isEddPending={isEddPending}
        isProcessing={isProcessing}
        riskLevel={riskLevel}
        showAddFundsModal={showAddFundsModal}
        setShowAddFundsModal={setShowAddFundsModal}
        onScreeningProgressComplete={() => {
          if (screeningStatus?.riskLevel) {
            setRiskLevel(screeningStatus.riskLevel);
          }
        }}
        onBackToDashboard={handleBackToDashboard}
        onProceedFromDownload={handleProceedFromDownload}
        onMatchReportPurchase={handleMatchReportPurchase}
        onSkipMatchReport={handleSkipMatchReport}
        onEddPurchase={handleEddPurchase}
        onSkipEddUpsell={handleSkipEddUpsell}
        onSkipEdd={handleSkipEdd}
        onDocRequest={handleDocRequest}
        onSkipDocRequest={handleSkipDocRequest}
        onViewReport={() => setFlowStep('COMPLETE')}
        onGoBackToPurchase={handleGoBackToPurchase}
      />
    </WizardLayout>
  );
}
